import React, { FC, useMemo, useState } from 'react'
import { DecryptedContact, DecryptedService } from '@icure/cardinal-sdk'
import { Collapse, CollapseProps, ConfigProvider, Dropdown, MenuProps } from 'antd'
import { RightOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { formatTimestampToHumanReadable } from '../../../../../../helpers/date'
import './index.less'
import { ConsultationContent } from '../ConsultationContent'
import { ConsultationHeader } from '../ConsultationHeader'
import { DecryptedPatient } from '@icure/cardinal-sdk/model.mjs'
import { DropdownRow } from '../../../../../common/DropdownRow'
import { deleteIcn, gearIcn } from '../../../../../../assets/CustomIcons'
import { IconButton } from '../../../../../common/IconButton'
import { ModalDeleteConsultation } from '../modals/ModalDeleteConsultation'

interface ConsultationProps {
  contact: DecryptedContact
  patient: DecryptedPatient
}

export enum ServicesTagsEnum {
  document = 'document',
  note = 'MS-FIXME-Note',
  sinister = 'SINISTER',
  dexter = 'DEXTER',
  diagnosis = 'diagnosis',
  diabeticMaculopathy = 'dme',
  diabeticRetinopathy = 'dr',
}

// keep constants outside the component to avoid re-allocating on each render
const DIABETIC_RETINOPATHY_SNOMED = new Set(['134395001', '4855003'] as const)
type ServiceLike = NonNullable<DecryptedContact['services']>[number]

export const Consultation: FC<ConsultationProps> = ({ contact, patient }) => {
  const { i18n } = useTranslation()
  const [isHeaderHovered, setHeaderHovered] = useState(false)
  const [isCollapseOpen, setCollapseOpen] = useState(false)
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)

  const indexedServices = useMemo(
    () =>
      (contact.services ?? []).map((service) => ({
        service: service as ServiceLike,
        tagSet: new Set((service.tags ?? []).map((t) => t.code).filter((i) => i !== undefined)),
        codeSet: new Set((service.codes ?? []).map((c) => c.code).filter((i) => i !== undefined)),
      })),
    [contact.services],
  )
  const hasAnyCode = (codes: Set<string>, targets: Set<string>) => {
    for (const t of targets) if (codes.has(t)) return true
    return false
  }
  const hasTag = (tags: Set<string>, tag: ServicesTagsEnum) => tags.has(tag)
  const hasAllTags = (tags: Set<string>, req: ServicesTagsEnum[]) => req.every((t) => tags.has(t))
  const findServiceBy = (pred: (i: (typeof indexedServices)[number]) => boolean) => indexedServices.find(pred)?.service

  const examinationsService = findServiceBy(({ codeSet }) => hasAnyCode(codeSet, DIABETIC_RETINOPATHY_SNOMED))
  const pdfReportService = findServiceBy(({ tagSet, service }) => hasTag(tagSet, ServicesTagsEnum.document) && service.content?.descr?.stringValue === 'report.pdf')
  const notesService = findServiceBy(({ tagSet }) => hasTag(tagSet, ServicesTagsEnum.note))
  const diagnosisService = findServiceBy(({ tagSet }) => hasTag(tagSet, ServicesTagsEnum.diagnosis))
  const oculusSinisterService = findServiceBy(({ tagSet }) => hasAllTags(tagSet, [ServicesTagsEnum.sinister, ServicesTagsEnum.document]))
  const oculusDexterService = findServiceBy(({ tagSet }) => hasAllTags(tagSet, [ServicesTagsEnum.dexter, ServicesTagsEnum.document]))

  const indexedExaminationServices = useMemo(
    () =>
      (examinationsService?.content?.en?.compoundValue ?? []).map((service) => ({
        examinationService: service as ServiceLike,
        examinationTagSet: new Set((service.tags ?? []).map((t) => t.code).filter((i) => i !== undefined)),
      })),
    [examinationsService],
  )
  const findExaminationServiceBy = (pred: (i: (typeof indexedExaminationServices)[number]) => boolean) => indexedExaminationServices.find(pred)?.examinationService

  const diabeticMaculopathySinisterService = findExaminationServiceBy(({ examinationTagSet }) =>
    hasAllTags(examinationTagSet, [ServicesTagsEnum.sinister, ServicesTagsEnum.diabeticMaculopathy]),
  )
  const diabeticMaculopathyDexterService = findExaminationServiceBy(({ examinationTagSet }) =>
    hasAllTags(examinationTagSet, [ServicesTagsEnum.dexter, ServicesTagsEnum.diabeticMaculopathy]),
  )

  const diabeticRetinopathyDexterService = findExaminationServiceBy(({ examinationTagSet }) =>
    hasAllTags(examinationTagSet, [ServicesTagsEnum.dexter, ServicesTagsEnum.diabeticRetinopathy]),
  )

  const diabeticRetinopathySinisterService = findExaminationServiceBy(({ examinationTagSet }) =>
    hasAllTags(examinationTagSet, [ServicesTagsEnum.sinister, ServicesTagsEnum.diabeticRetinopathy]),
  )

  const getExaminationValue = (service?: DecryptedService) => {
    const measureValue = service?.content?.en?.measureValue
    return {
      ref: measureValue?.ref,
      value: measureValue?.value,
    }
  }

  const diabeticMaculopathyExaminationValue = {
    dexter: getExaminationValue(diabeticMaculopathyDexterService),
    sinister: getExaminationValue(diabeticMaculopathySinisterService),
  }

  const diabeticRetinopathyExaminationValue = {
    dexter: getExaminationValue(diabeticRetinopathyDexterService),
    sinister: getExaminationValue(diabeticRetinopathySinisterService),
  }

  const getOverflowMenu = () => {
    const items: MenuProps['items'] = [
      {
        key: 'delete_consultation',
        danger: true,
        label: <DropdownRow icon={deleteIcn} label={'Delete consultation'} />,
      },
    ]

    const onClick: MenuProps['onClick'] = ({ key, domEvent }) => {
      domEvent.stopPropagation()

      switch (key) {
        case 'delete_consultation': {
          setDeleteModalOpen(true)
          break
        }
      }
    }
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <Dropdown menu={{ items, onClick }} placement="bottomRight" arrow trigger={['click']}>
          <div className={`introCollapse__expandIcon`}>
            <IconButton icon={gearIcn} withContainer={true} />
          </div>
        </Dropdown>
      </div>
    )
  }

  const items: CollapseProps['items'] = [
    {
      key: contact.id,
      label: (
        <ConsultationHeader
          diabeticMaculopathyExaminationValue={diabeticMaculopathyExaminationValue}
          diabeticRetinopathyExaminationValue={diabeticRetinopathyExaminationValue}
          dateOfExamination={formatTimestampToHumanReadable(contact.openingDate, i18n.language)}
          pdfReportService={pdfReportService}
          handleOnMouseEnter={() => setHeaderHovered(true)}
          handleOnMouseLeave={() => setHeaderHovered(false)}
          patient={patient}
          contact={contact}
        />
      ),
      children: (
        <ConsultationContent
          diabeticMaculopathyExaminationValue={diabeticMaculopathyExaminationValue}
          diabeticRetinopathyExaminationValue={diabeticRetinopathyExaminationValue}
          oculusDexterService={oculusDexterService}
          oculusSinisterService={oculusSinisterService}
          notesService={notesService}
          diagnosisService={diagnosisService}
        />
      ),
      extra: getOverflowMenu(),
    },
  ]

  const ConsultationsCollapseTheme = {
    token: {
      colorPrimary: '#084B83',
    },
    components: {
      Collapse: {
        // headerBg: isCollapseOpen ? '#DCE9F7' : isHeaderHovered ? '#EDF4FB' : '#F6FAFF',
        // headerBg: isCollapseOpen ? '#DDEEFF' : isHeaderHovered ? '#DDEEFF' : '#EAF4FF',
        headerBg: isCollapseOpen ? '#DDEEFF' : isHeaderHovered ? '#DDEEFF' : '#EAF4FF',

        contentBg: '#F6FAFF !important',

        // Height of the expand icon
        fontHeight: '32px',
        borderlessContentPadding: '0',
        contentPadding: '0',
      },
    },
  }

  return (
    <ConfigProvider theme={ConsultationsCollapseTheme}>
      <Collapse
        className={`consultationCollapse ${isCollapseOpen ? 'active' : ''}`}
        bordered={false}
        onChange={(key) => (key.includes(contact.id) ? setCollapseOpen(true) : setCollapseOpen(false))}
        expandIcon={({ isActive }) => (
          <div className={`consultationCollapse__expandIcon ${isActive ? 'active' : ''}`}>
            <RightOutlined rotate={isActive ? 90 : 0} />
          </div>
        )}
        items={items}
        style={{ borderRadius: '0' }}
      />
      {isDeleteModalOpen && <ModalDeleteConsultation contact={contact} isVisible={isDeleteModalOpen} onClose={() => setDeleteModalOpen(false)} />}
    </ConfigProvider>
  )
}
